import {
  User, Shield, Users, Target, Activity, Palette, Trash2, Mail, Workflow, FileText, Terminal, Database, Briefcase,
} from 'lucide-react'
import { createElement } from 'react'
import type { Role } from '@/types'
import { INSTANCE_PROFILE, type InstanceProfile } from '@/config/instanceProfile'
import { isSuperAdminEmail } from '@/utils/superAdmin'

export type ConfigTabId =
  | 'account'
  | 'users'
  | 'plans'
  | 'sources'
  | 'finance'
  | 'comissoes'
  | 'email'
  | 'security'
  | 'recycle'
  | 'appearance'
  | 'esforco'
  | 'fluxos'
  | 'audit'
  | 'logs'
  | 'backup'
  | 'gestao'

export type ConfigTab = {
  id: ConfigTabId
  label: string
  icon: React.ReactNode
  roles: Role[]
}

const icon = (el: typeof User, size = 14) => createElement(el, { size })

export const CONFIGURACAO_TABS: ConfigTab[] = [
  { id: 'account', label: 'Minha Conta', icon: icon(User), roles: ['admin', 'bdr', 'closer', 'financeiro', 'pos_venda'] },
  { id: 'users', label: 'Usuários', icon: icon(Users), roles: ['admin'] },
  { id: 'gestao', label: 'Gestão', icon: icon(Briefcase), roles: ['admin', 'manager'] },
  { id: 'esforco', label: 'Esforço Time', icon: icon(Activity), roles: ['admin'] },
  { id: 'sources', label: 'Origens', icon: icon(Target), roles: ['admin', 'bdr', 'closer', 'financeiro', 'pos_venda'] },
  { id: 'finance', label: 'Financeiro', icon: icon(Activity), roles: ['admin', 'financeiro', 'bdr', 'closer'] },
  { id: 'email', label: 'Email', icon: icon(Mail), roles: ['admin', 'bdr', 'financeiro'] },
  { id: 'security', label: 'Segurança', icon: icon(Shield), roles: ['admin'] },
  { id: 'recycle', label: 'Lixeira', icon: icon(Trash2), roles: ['admin'] },
  { id: 'fluxos', label: 'Fluxos', icon: icon(Workflow), roles: ['admin', 'bdr', 'closer', 'financeiro', 'pos_venda'] },
  { id: 'appearance', label: 'Aparência', icon: icon(Palette), roles: ['admin', 'bdr', 'closer', 'financeiro', 'pos_venda'] },
  { id: 'audit', label: 'Auditoria', icon: icon(FileText), roles: ['admin'] },
  { id: 'logs', label: 'Logs', icon: icon(Terminal), roles: ['admin'] },
  { id: 'backup', label: 'Backup', icon: icon(Database), roles: ['admin'] },
]

/** Abas ocultas na Rocket (Agrada-only ou desativadas por enquanto). */
const ROCKET_BLOCKED_CONFIG_TABS = new Set<ConfigTabId>(['gestao', 'security', 'esforco'])

/** Auditoria / Logs / Backup — somente super admins. */
const SUPER_ADMIN_ONLY_CONFIG_TABS = new Set<ConfigTabId>(['audit', 'logs', 'backup'])

/**
 * Gestor na Rocket: só abas que a API já permite operar.
 * (Usuários/Backup/Aparência/etc. exigem ADMIN e quebravam a UX.)
 */
const ROCKET_MANAGER_CONFIG_TABS = new Set<ConfigTabId>([
  'account',
  'sources',
  'finance',
  'fluxos',
])

export type ConfigTabViewer = {
  email?: string | null
}

export function getVisibleConfigurationTabs(
  role: Role,
  profile: InstanceProfile = INSTANCE_PROFILE,
  viewer?: ConfigTabViewer | null,
): ConfigTab[] {
  const superAdmin = role === 'admin' && isSuperAdminEmail(viewer?.email)

  const filterSuperAdminTabs = (tabs: ConfigTab[]) =>
    tabs.filter((tab) => superAdmin || !SUPER_ADMIN_ONLY_CONFIG_TABS.has(tab.id))

  if (profile === 'rocket' && role === 'admin') {
    return filterSuperAdminTabs(
      CONFIGURACAO_TABS.filter((tab) => !ROCKET_BLOCKED_CONFIG_TABS.has(tab.id)),
    )
  }

  if (profile === 'rocket' && role === 'manager') {
    return CONFIGURACAO_TABS.filter(
      (tab) =>
        ROCKET_MANAGER_CONFIG_TABS.has(tab.id) && !ROCKET_BLOCKED_CONFIG_TABS.has(tab.id),
    )
  }

  return filterSuperAdminTabs(
    CONFIGURACAO_TABS.filter(
      (tab) =>
        tab.roles.includes(role) &&
        (profile !== 'rocket' || !ROCKET_BLOCKED_CONFIG_TABS.has(tab.id)),
    ),
  )
}
